import React, { useEffect, useState } from 'react';
import { fetchCrossChainChart, ChainTransaction, ChainDestination } from '@/services/api';
import { BRIDGE_NETWORKS } from '@/configs/bridge.config';
import { RiLoader4Line } from '@remixicon/react';
import theme from '@/theme/theme';

interface BridgeStatsChartProps {
    metricType: 'volume' | 'transfers';
    timePeriod: '24h' | '7d' | '30d' | 'all';
}

interface ChainData {
    id: number;
    name: string;
    icon: string;
    volume: number;
    percentage: number;
}

interface DestinationData extends ChainData {
    sourceVolume: number; // Volume coming from selected source
    sourcePercentage: number; // Percentage of selected source's total
}

// Helper function to get chain info from BRIDGE_NETWORKS
function getChainInfo(chainId: number) {
    const network = BRIDGE_NETWORKS.find(n => n.id === chainId);
    return {
        name: network?.name || `Chain ${chainId}`,
        icon: network?.iconURI || '',
    };
}

// Helper to format numbers with K/M suffix
function formatNumber(num: number, metricType: 'volume' | 'transfers'): string {
    if(metricType === 'transfers') return (
        num.toLocaleString()
    )
    if (num >= 1000000) {
        return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
        return `$${Math.round(num / 1000)}K`;
    }
    return `$${Math.round(num)}`;
}

export function BridgeStatsChart({ metricType, timePeriod }: BridgeStatsChartProps) {
    const [sourceChains, setSourceChains] = useState<ChainData[]>([]);
    const [allDestinations, setAllDestinations] = useState<Map<number, Map<number, DestinationData>>>(new Map());
    const [hoveredSource, setHoveredSource] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;

        async function loadData() {
            try {
                setIsLoading(true);
                setError(null);

                const response = await fetchCrossChainChart(metricType, timePeriod);

                if (!mounted) return;

                if (!response.status || !response.data?.txs) {
                    throw new Error(response.message || 'Failed to load chart data');
                }

                const sources: ChainData[] = [];
                const destMap = new Map<number, Map<number, DestinationData>>();

                response.data.txs.forEach((tx: ChainTransaction) => {
                    const sourceInfo = getChainInfo(tx.chain);
                    const sourceVolume = parseFloat(tx.volume);

                    sources.push({
                        id: tx.chain,
                        name: sourceInfo.name,
                        icon: sourceInfo.icon,
                        volume: sourceVolume,
                        percentage: tx.percentage * 100,
                    });

                    // Create destination map for this source
                    const sourceDestMap = new Map<number, DestinationData>();

                    tx.destinations.forEach((dest: ChainDestination) => {
                        const destInfo = getChainInfo(dest.chain);
                        const weight = parseFloat(dest.volume);

                        if (weight > 0) {
                            sourceDestMap.set(dest.chain, {
                                id: dest.chain,
                                name: destInfo.name,
                                icon: destInfo.icon,
                                volume: weight,
                                percentage: dest.percentage,
                                sourceVolume: weight,
                                sourcePercentage: dest.percentage,
                            });
                        }
                    });

                    destMap.set(tx.chain, sourceDestMap);
                });

                setSourceChains(sources);
                setAllDestinations(destMap);

                // Set first source as hovered by default
                if (sources.length > 0) {
                    setHoveredSource(sources[0].id);
                }
            } catch (err) {
                if (!mounted) return;
                setError(err instanceof Error ? err.message : 'Failed to load chart data');
                console.error('Error loading bridge stats:', err);
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        }

        loadData().then();

        return () => {
            mounted = false;
        };
    }, [metricType, timePeriod]);

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 min-h-[600px]">
                <RiLoader4Line className={`size-12 text-[${theme.colors.primary.hex}] animate-spin mb-4`} />
                <p className="text-muted-foreground">Loading bridge statistics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-20 min-h-[600px]">
                <p className="text-red-500 mb-2">Failed to load chart</p>
                <p className="text-muted-foreground text-sm">{error}</p>
            </div>
        );
    }

    if (sourceChains.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 min-h-[600px]">
                <p className="text-muted-foreground">No bridge data available for this period</p>
            </div>
        );
    }

    // Get destinations for hovered source
    const currentDestinations = hoveredSource
        ? Array.from(allDestinations.get(hoveredSource)?.values() || [])
        : [];

    // Calculate SVG dimensions
    const rowHeight = 56;
    const rowGap = 4;
    const svgHeight = Math.max(sourceChains.length, currentDestinations.length) * (rowHeight + rowGap);

    return (
        <div className="w-full relative">
            <div className="w-full overflow-x-auto pb-4">
                <div className="grid grid-cols-[380px_1fr_380px] gap-6 min-w-[1024px]">
                    {/* Source Column */}
                    <div className="space-y-1">
                        <div className="text-xs font-semibold text-white/30 mb-4 px-3 uppercase tracking-wide">Source</div>
                        {sourceChains.map((chain, index) => {
                            const isHovered = hoveredSource === chain.id;
                            return (
                                <div
                                    key={`source-${chain.id}`}
                                    className={`flex items-center justify-between px-4 py-4 rounded-lg transition-all cursor-pointer ${isHovered
                                        ? 'bg-gradient-to-r from-purple-900/50 to-blue-900/50 shadow-lg shadow-purple-500/20'
                                        : 'bg-[#1A1A1A] hover:bg-[#222]'
                                        }`}
                                    style={{ height: `${rowHeight}px` }}
                                    onMouseEnter={() => setHoveredSource(chain.id)}
                                    onClick={() => setHoveredSource(chain.id)}
                                >
                                    <div className="flex items-center gap-3 flex-1">
                                        <img src={chain.icon} alt={chain.name} className="w-6 h-6 rounded-full" />
                                        <span className="text-sm font-semibold text-white">{chain.name}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-semibold text-white/90">
                                            {formatNumber(chain.volume, metricType)}
                                        </span>
                                        <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-yellow-300/90 text-black min-w-[60px] text-center">
                                            {chain.percentage.toFixed(2)}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Center Flow Visualization */}
                    <div className="relative flex flex-col w-full gap-1">
                        {/* Invisible spacer to match the "Source/Target" header height */}
                        <div className="text-xs font-semibold text-transparent mb-4 px-3 uppercase tracking-wide select-none">Spacer</div>
                        <svg
                            width="100%"
                            height={svgHeight}
                            viewBox={`0 0 800 ${svgHeight}`}
                            preserveAspectRatio="none"
                            className="overflow-visible w-full"
                        >
                            <defs>
                                <linearGradient id="flowGradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="800" y2="0">
                                    <stop offset="0%" style={{ stopColor: '#8B5CF6', stopOpacity: 0.8 }} />
                                    <stop offset="100%" style={{ stopColor: '#3B82F6', stopOpacity: 0.8 }} />
                                </linearGradient>
                                <linearGradient id="flowGradientDim" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="800" y2="0">
                                    <stop offset="0%" style={{ stopColor: '#8B5CF6', stopOpacity: 0.1 }} />
                                    <stop offset="100%" style={{ stopColor: '#3B82F6', stopOpacity: 0.1 }} />
                                </linearGradient>
                            </defs>

                            {sourceChains.map((source, sourceIndex) => {
                                const destinations = allDestinations.get(source.id);
                                if (!destinations) return null;

                                // Sort destinations so largest volume renders first (at the back), smallest on top
                                const sortedDestinations = Array.from(destinations.values()).sort((a, b) => b.volume - a.volume);

                                return sortedDestinations.map((dest, destIndex) => {
                                    const targetIndex = currentDestinations.findIndex(d => d.id === dest.id);

                                    // Only draw lines for the currently selected source
                                    if (source.id !== hoveredSource) return null;
                                    if (targetIndex === -1) return null;

                                    const sourceY = sourceIndex * (rowHeight + rowGap) + rowHeight / 2;
                                    const targetY = targetIndex * (rowHeight + rowGap) + rowHeight / 2;

                                    // Calculate stroke width based on volume
                                    const maxVolume = Math.max(...Array.from(destinations.values()).map(d => d.volume));
                                    const strokeWidth = 2 + (dest.volume / maxVolume) * 24;

                                    // Create smooth curve
                                    const curve = `M 0,${sourceY} C 320,${sourceY} 480,${targetY} 800,${targetY}`;

                                    return (
                                        <path
                                            key={`flow-${source.id}-${dest.id}-${destIndex}`}
                                            d={curve}
                                            stroke="url(#flowGradient)"
                                            strokeWidth={strokeWidth}
                                            fill="none"
                                            className="transition-all duration-300"
                                            strokeLinecap="round"
                                            style={{
                                                opacity: 1,
                                            }}
                                        />
                                    );
                                });
                            })}
                        </svg>

                        {/* Center watermark */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="text-white/5 text-2xl font-bold tracking-[0.3em]">
                                LARISSA BRIDGE
                            </div>
                        </div>
                    </div>

                    {/* Target Column */}
                    <div className="space-y-1">
                        <div className="text-xs font-semibold text-white/30 mb-4 px-3 uppercase tracking-wide">Target</div>
                        {currentDestinations.map((chain) => {
                            return (
                                <div
                                    key={`target-${chain.id}`}
                                    className="relative flex items-center justify-between px-4 py-4 rounded-lg bg-[#1A1A1A] overflow-hidden"
                                    style={{ height: `${rowHeight}px` }}
                                >
                                    {/* Background progress bar */}
                                    <div
                                        className="absolute inset-0 bg-gradient-to-r from-purple-900/30 to-transparent transition-all duration-300"
                                        style={{
                                            width: `${chain.sourcePercentage}%`,
                                        }}
                                    />

                                    <div className="flex items-center gap-3 flex-1 relative z-10">
                                        <img src={chain.icon} alt={chain.name} className="w-6 h-6 rounded-full" />
                                        <span className="text-sm font-semibold text-white">{chain.name}</span>
                                    </div>
                                    <div className="flex items-center gap-3 relative z-10">
                                        <span className="text-sm font-semibold text-white/90">
                                            {formatNumber(chain.sourceVolume, metricType)}
                                        </span>
                                        <span className="text-xs font-bold px-2.5 py-1 rounded-md bg-green-400/90 text-black min-w-[60px] text-center">
                                            {chain.sourcePercentage.toFixed(2)}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
